/**
 * The Day view's panel (and the Agenda's, for a row): the selected task
 * (or Site-row bar) in words,
 * with the actions this viewer may take on it. It floats over the right
 * of the timeline while something is selected (2026-10-30); × or Esc
 * closes it (`CalendarLanes`).
 *
 * NO NEW PERMISSIONS. Every action is a callback the screen withholds
 * when the viewer's role may not do it -- the same gates the Tasks screen
 * already applies (`scheduleEditsAllowed`, `maintenanceEditsAllowed`) --
 * and a live task's controls are `TaskControlActions`, rendered through
 * `renderTaskActions` with its own capability checks. A withheld action
 * is absent, not disabled.
 *
 * "Edit schedule…" (2026-10-30) is the one drawn-but-off action: in a
 * "View as" preview it is shown disabled with the preview line, as the
 * Agenda's schedule panel shows its actions, so the previewed role's
 * console is what is seen. It opens the schedule editor with "Applies
 * from" set to THIS run's day -- the run the operator was looking at.
 *
 * A blocked time names who booked it ("Blocked by"), in the words the
 * schedule panel uses for who made a schedule (`creatorLabel`). "Remove
 * this window" asks first -- Keep / Remove it, as the Agenda's Remove
 * does (2026-10-31): removing one lets robots be sent into that period
 * at once, and one stray click did it.
 *
 * A task a schedule fired names it only by the name it had then
 * (`calendar:<name>`), and an edit can rename it or leave two of one
 * name -- the old time kept on a carry-over and the schedule it came
 * from -- so its schedule is the one the fire ledger names, else the
 * name read against the task's start (`scheduleOfItem`). "Pause
 * schedule" on either of the two asks whether the other pauses too
 * (`planPartners`). What a Pause came to is said on the panel's status
 * line (2026-10-31): a refusal of one schedule was said nowhere before,
 * outside the scope dialog.
 *
 * A run of a schedule that has ENDED -- every run before the change day
 * of a carry-over, once that day has passed -- acts on the schedule
 * that took over from it (`takenOverBy`): pausing or editing the ended
 * one would change nothing that runs. The From and Repeats rows still
 * name the one that made this run. With nothing taking over, Pause is
 * gone and Edit stays: moving the end later is how an ended schedule is
 * brought back.
 */
import { useRef, useState, type ReactNode, type RefObject } from 'react';
import type { TaskState } from '../../../api/rmf-client';
import type { MaintenanceWindow, TaskSchedule } from '../../../api/v1';
import { WINDOW_KIND_WORDS } from '../../Devices/lift-rules-words';
import { describeRule, isOneShotRule } from '../rrule';
import type { LiftCalendar } from './useLiftCalendar';
import { hasScheduleHistory } from '../schedule-edit-summary';
import {
  formatZonedClock,
  formatZonedDateTime,
  timeZoneLabel,
  wallClockToEpochMs,
  zonedParts,
} from '../zoned-time';
import {
  DIDNT_FINISH,
  OUTCOME_LABEL,
  outcomeNote,
  paintOf,
  sourceText,
  type LaneItem,
} from './lane-items';
import { laneVars } from './lane-style';
import { KeptPauseConfirm } from './KeptPauseConfirm';
import { creatorLabel, standaloneLabel } from './people';
import { useRevealOnMount } from './reveal';
import { hasEnded, planPartners, scheduleOfItem, takenOverBy } from './schedule-span';
import { dateKeyOf, formatDuration, formatKey, formatRange } from './lane-time';
import type { SiteBar } from './site-row';
import { PanelStatus } from './PanelStatus';
import { robotText } from './phone-model';
import { PREVIEW_LINE, type ToggleOutcome } from './useScheduleActions';

/** What opening the schedule editor needs: which schedule, the run it
 *  was opened from (its "Applies from" day), and the control to give
 *  focus back to. */
export interface EditScheduleRequest {
  scheduleId: string;
  applyFromMs?: number | null;
  trigger?: HTMLElement | null;
}

export interface LanePanelProps {
  item: LaneItem | null;
  site: SiteBar | null;
  /** The selection is set but no longer in view (paged away, filtered). */
  stale: boolean;
  timeZone: string;
  schedules: readonly TaskSchedule[];
  robotLabel: (key: string | null) => string | null;
  onClose: () => void;
  onOpenTask?: (item: LaneItem) => void;
  onSkipOccurrence?: (item: LaneItem) => void;
  /** Move a scheduled occurrence; the screen then asks "this one or the
   *  series?" exactly as the old drag did. */
  onMoveOccurrence?: (item: LaneItem, newStartMs: number) => void;
  /** Pause the run's schedule (and `alsoIds`, the other rows of its
   *  plan). When it answers with what that came to, the panel says it
   *  on its status line. */
  onPauseSeries?: (
    scheduleId: string,
    alsoIds?: readonly string[],
  ) => void | Promise<ToggleOutcome>;
  /** Open the schedule editor; withheld when this viewer may not edit
   *  schedules, and on a phone. */
  onEditSchedule?: (req: EditScheduleRequest) => void;
  /** A "View as" preview: Edit is drawn but off. */
  actionsOff?: boolean;
  /** The signed-in user's subject, so a blocked time they booked says
   *  "you". */
  mySub?: string | null;
  onDeleteMaintenance?: (w: MaintenanceWindow) => void | Promise<boolean>;
  /** Lift rules on the calendar. Its `remove` and `skip` come only when this
   *  viewer may change them; a run's details are for everyone. */
  liftCalendar?: Pick<LiftCalendar, 'windowOf'> & Partial<Pick<LiftCalendar, 'remove' | 'skip'>>;
  renderTaskActions?: (task: TaskState) => ReactNode;
}

function terminal(t: TaskState): boolean {
  return t.status === 'completed' || t.status === 'failed' || t.status === 'cancelled';
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      className="nx-lane-panel__close"
      onClick={onClose}
      aria-label="Close the panel (Esc)"
      title="Close (Esc)"
      data-testid="cal-panel-close"
    >
      ×
    </button>
  );
}

function Row({ k, v, strong = false }: { k: string; v: ReactNode; strong?: boolean }) {
  return (
    <div className="nx-lane-panel__kv">
      <span className="nx-lane-panel__k">{k}</span>
      <span className={`nx-lane-panel__v${strong ? ' is-strong' : ''}`}>{v}</span>
    </div>
  );
}

export function LanePanel(props: LanePanelProps) {
  const { item, site, timeZone } = props;
  const [confirmPause, setConfirmPause] = useState<string | null>(null);
  // What the last Pause from this panel came to, for the run it was
  // pressed on. Another run selected: gone. The run gone from view --
  // as a paused schedule's runs go -- it stays, and says why.
  const [paused, setPaused] = useState<{
    itemId: string;
    pending: boolean;
    outcome: ToggleOutcome | null;
  } | null>(null);
  if (paused && item && item.id !== paused.itemId) setPaused(null);
  const pauseLine = paused && (
    <PanelStatus
      status={
        paused.pending || !paused.outcome
          ? { pending: true, message: null, tone: 'ok' }
          : { pending: false, ...paused.outcome }
      }
      testId="cal-panel-status-line"
    />
  );

  if (site?.liftRule) return <LiftRulePanel {...props} site={site} />;
  if (site) return <SitePanel {...props} site={site} />;

  if (!item) {
    return (
      <aside className="nx-lane-panel" aria-label="Selected task" data-testid="cal-panel">
        <div className="nx-lane-panel__top">
          <span className="nx-lane-panel__eyebrow">Selected task</span>
          <CloseButton onClose={props.onClose} />
        </div>
        <p className="nx-lane-panel__hint">
          {props.stale
            ? 'The task you selected is not in this view any more.'
            : 'Select a task to see it here.'}
        </p>
        {props.stale && pauseLine}
      </aside>
    );
  }

  const schedule = scheduleOfItem(item, props.schedules, Date.now());
  const from = schedule ? `Schedule · ${schedule.name}` : sourceText(item.source);
  const rule = schedule
    ? describeRule(
        schedule.rrule,
        schedule.dtstart_ms,
        schedule.until_ms,
        schedule.timezone || timeZone,
        // The calendar's "Times in ..." line already names the site's zone.
        { siteTimeZone: timeZone },
      )
    : null;
  // The phone's words for it (`robotText`): "Any cleaning robot" for a
  // run with none pinned, "Not assigned yet" for a task without one.
  const robot = robotText(item, props.robotLabel);
  const length = item.endMs - item.startMs;
  const live = item.live;
  const fail = DIDNT_FINISH.has(item.outcome);
  const canOpen = Boolean(props.onOpenTask) && Boolean(item.live || item.completion);
  const canSkip =
    Boolean(props.onSkipOccurrence) && item.kind === 'occurrence' && item.outcome === 'scheduled';
  const canMove =
    Boolean(props.onMoveOccurrence) && item.kind === 'occurrence' && item.outcome === 'scheduled';
  // What Pause and Edit act on: the schedule, or -- once it has ended --
  // the one that took over from it.
  const nowMs = Date.now();
  const ended = schedule !== undefined && hasEnded(schedule, nowMs);
  const successor = ended ? takenOverBy(schedule!, props.schedules, nowMs) : undefined;
  const acting = ended ? successor : schedule;
  const editTarget = successor ?? schedule;
  const canPause =
    Boolean(props.onPauseSeries) && item.kind === 'occurrence' && acting?.enabled === true;
  // The other row of its plan, still running: Pause asks first.
  const partners = canPause ? planPartners(acting!, props.schedules, nowMs, timeZone) : null;
  // A run of a repeating schedule, planned or done, or a task one fired:
  // the schedule is what is edited. A one-off booking is a date, moved
  // with "Move to" instead.
  const canEdit =
    Boolean(props.onEditSchedule) && Boolean(editTarget) && !isOneShotRule(editTarget!.rrule);
  const note = fail ? item.detail : outcomeNote(item);
  const pausePending = paused?.pending === true;
  const pause = (alsoIds?: readonly string[]) => {
    const itemId = item.id;
    const id = acting!.schedule_id;
    const sent = alsoIds ? props.onPauseSeries!(id, alsoIds) : props.onPauseSeries!(id);
    if (!sent) return;
    setPaused({ itemId, pending: true, outcome: null });
    void sent.then((outcome) =>
      setPaused((cur) => (cur?.itemId === itemId ? { itemId, pending: false, outcome } : cur)),
    );
  };

  return (
    <aside className="nx-lane-panel" aria-label="Selected task" data-testid="cal-panel">
      <div className="nx-lane-panel__top">
        <span className="nx-lane-panel__eyebrow">Selected task</span>
        <CloseButton onClose={props.onClose} />
      </div>
      <h3 className="nx-lane-panel__title" data-testid="cal-panel-title">
        {item.title}
      </h3>
      <span className="nx-lane-panel__type" style={laneVars(paintOf(item))}>
        <span className="nx-lane-panel__swatch" aria-hidden="true" />
        {item.typeLabel} · {robot}
      </span>
      <div className="nx-lane-panel__sep">
        <Row
          k="When"
          v={
            <>
              {dateKeyOf(item.startMs, timeZone) !== dateKeyOf(item.endMs, timeZone)
                ? `${formatZonedDateTime(item.startMs, timeZone)} → ${formatZonedDateTime(item.endMs, timeZone)}`
                : `${formatKey(dateKeyOf(item.startMs, timeZone))}, ${formatRange(item.startMs, item.endMs, timeZone)}`}
            </>
          }
        />
        <Row
          k="Length"
          v={`${length > 0 ? formatDuration(length) : 'did not start'}${item.estimated && length > 0 ? ' (estimate)' : ''}`}
        />
        <Row
          k="Status"
          strong
          v={
            <span className={fail ? 'nx-lane-panel__fail' : ''} data-testid="cal-panel-status">
              {OUTCOME_LABEL[item.outcome]}
            </span>
          }
        />
        {/* Only for High: every job is Normal unless somebody chose. */}
        {item.high && (
          <Row k="Priority" v={<span data-testid="cal-panel-priority">High</span>} />
        )}
        <Row k="From" v={from} />
        {rule && <Row k="Repeats" v={rule} />}
        {item.taskId && (
          <Row k="Task" v={<code className="nx-lane-panel__id">{item.taskId}</code>} />
        )}
      </div>
      {note && (
        <p className={`nx-lane-panel__note${fail ? ' is-fail' : ''}`} data-testid="cal-panel-note">
          {note}
        </p>
      )}
      <div className="nx-lane-panel__actions">
        {canOpen && (
          <button
            type="button"
            className="nx-lane-btn"
            onClick={() => props.onOpenTask!(item)}
            data-testid="cal-panel-open"
          >
            Open task
          </button>
        )}
        {canSkip && (
          <button
            type="button"
            className="nx-lane-btn"
            onClick={() => props.onSkipOccurrence!(item)}
            data-testid="cal-panel-skip"
          >
            Skip this run
          </button>
        )}
        {canPause && (
          <button
            type="button"
            className="nx-lane-btn"
            disabled={pausePending}
            onClick={() => (partners ? setConfirmPause(item.id) : pause())}
            title="Stop every future run. Its times and history are kept."
            data-testid="cal-panel-pause"
          >
            Pause schedule
          </button>
        )}
        {canEdit && (
          <button
            type="button"
            className="nx-lane-btn"
            disabled={props.actionsOff}
            title={
              props.actionsOff
                ? PREVIEW_LINE
                : successor
                  ? 'This run’s schedule has ended. Change the one that took over from it: its time, pattern, place, name or end.'
                  : // A schedule that has not run yet has no "from a day
                    // on": a new time applies to every run.
                    hasScheduleHistory(editTarget!, nowMs)
                    ? 'Change this schedule from this run’s day on: its time, pattern, place, name or end.'
                    : 'Change this schedule: its time, pattern, place, name or end.'
            }
            onClick={(e) =>
              props.onEditSchedule!({
                scheduleId: editTarget!.schedule_id,
                applyFromMs: item.occurrence?.original_start_ms ?? item.startMs,
                trigger: e.currentTarget,
              })
            }
            data-testid="cal-panel-edit-schedule"
          >
            Edit schedule…
          </button>
        )}
      </div>
      {canPause && partners && confirmPause === item.id && (
        <KeptPauseConfirm
          text={partners.text}
          list={partners.list}
          count={partners.rows.length}
          disabled={pausePending}
          onBoth={() => {
            setConfirmPause(null);
            pause(partners.rows.map((k) => k.schedule_id));
          }}
          onOne={() => {
            setConfirmPause(null);
            pause();
          }}
          onCancel={() => setConfirmPause(null)}
          className="nx-sched-panel__confirm"
          noteClass="nx-lane-panel__note"
          actionsClass="nx-lane-panel__actions"
          btnClass="nx-lane-btn"
          testId="cal-panel-pause-confirm"
        />
      )}
      {pauseLine}
      {canEdit && props.actionsOff && (
        <p className="nx-lane-panel__note is-hold" data-testid="cal-panel-preview">
          {PREVIEW_LINE}
        </p>
      )}
      {canMove && (
        <MoveOccurrence
          key={item.id}
          item={item}
          timeZone={timeZone}
          onMove={props.onMoveOccurrence!}
        />
      )}
      {live && !terminal(live) && props.renderTaskActions && (
        <div className="nx-lane-panel__controls" data-testid="cal-panel-controls">
          {props.renderTaskActions(live)}
        </div>
      )}
    </aside>
  );
}

/** "Move to HH:MM" for one scheduled occurrence, on its own day, on the
 *  site's clock. The keyboard's way to do what the old grid's drag did. */
function MoveOccurrence({
  item,
  timeZone,
  onMove,
}: {
  item: LaneItem;
  timeZone: string;
  onMove: (item: LaneItem, newStartMs: number) => void;
}) {
  const [time, setTime] = useState(() => formatZonedClock(item.startMs, timeZone));
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time);
  const day = zonedParts(item.startMs, timeZone);
  const target = m
    ? wallClockToEpochMs({ ...day, hour: Number(m[1]), minute: Number(m[2]), second: 0 }, timeZone)
    : null;
  return (
    <div className="nx-lane-panel__move">
      <label>
        <span>Move to</span>
        <input
          type="time"
          step={900}
          value={time}
          onChange={(e) => setTime(e.target.value)}
          data-testid="cal-panel-move-time"
        />
      </label>
      <button
        type="button"
        className="nx-lane-btn"
        disabled={target === null || target === item.startMs}
        onClick={() => target !== null && onMove(item, target)}
        data-testid="cal-panel-move"
      >
        Move…
      </button>
    </div>
  );
}

/** A run of a lift rule: what it does, and Skip or Remove behind Keep. */
function LiftRulePanel(props: LanePanelProps & { site: SiteBar }) {
  const { site, timeZone, onClose } = props;
  const run = site.liftRule!;
  const win = props.liftCalendar?.windowOf(run.window_id) ?? null;
  const repeats = Boolean(win?.rrule);
  const [asking, setAsking] = useState<'skip' | 'remove' | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const askRef = useRef<HTMLButtonElement>(null);
  const { remove, skip } = props.liftCalendar ?? {};
  const act = (answer: Promise<string | null>) => {
    setAsking(null);
    setRefusal(null);
    void answer.then((why) => {
      if (why) setRefusal(why);
      else onClose();
    });
  };
  const by = win?.created_by_email ?? null;
  const pattern = win?.rrule
    ? describeRule(win.rrule, win.starts_at_ms, win.repeat_until_ms, win.timezone, {
        siteTimeZone: timeZone,
      })
    : null;
  return (
    <aside className="nx-lane-panel" aria-label="Selected lift rule" data-testid="cal-panel">
      <div className="nx-lane-panel__top">
        <span className="nx-lane-panel__eyebrow">{run.lift}</span>
        <CloseButton onClose={onClose} />
      </div>
      <h3 className="nx-lane-panel__title" data-testid="cal-panel-title">
        {WINDOW_KIND_WORDS[run.kind]}
      </h3>
      <div className="nx-lane-panel__sep">
        <Row
          k="When"
          v={`${formatZonedDateTime(site.startMs, timeZone)} → ${formatZonedDateTime(site.endMs, timeZone)} ${timeZoneLabel(timeZone, site.startMs)}`}
        />
        {pattern && <Row k="Repeats" v={pattern} />}
        <Row k="Reason" v={run.reason || '—'} />
        {by && <Row k="Set up by" v={<span data-testid="cal-panel-lift-by">{by}</span>} />}
      </div>
      <p className="nx-lane-panel__note">
        {run.kind === 'block_use'
          ? 'Robots do not use this lift during this time. A robot already riding finishes its ride first.'
          : 'Robots may ride this lift between floors during this time.'}
      </p>
      {refusal && (
        <p className="nx-lane-panel__note is-fail" role="alert" data-testid="cal-panel-lift-error">
          {refusal}
        </p>
      )}
      {asking ? (
        <RemoveConfirm
          back={askRef}
          removeLabel={asking === 'skip' ? 'Skip it' : 'Remove it'}
          onKeep={() => setAsking(null)}
          onRemove={() => {
            if (asking === 'skip') act(skip!(run.window_id, run.starts_at_ms));
            else act(remove!(run.window_id));
          }}
        />
      ) : (
        (remove || skip) && (
          <div className="nx-lane-panel__actions">
            {repeats && skip && (
              <button
                ref={askRef}
                type="button"
                className="nx-lane-btn"
                onClick={() => setAsking('skip')}
                data-testid="cal-panel-skip-lift"
              >
                Skip this one
              </button>
            )}
            {remove && (
              <button
                ref={repeats ? undefined : askRef}
                type="button"
                className="nx-lane-btn nx-lane-btn--danger"
                onClick={() => setAsking('remove')}
                data-testid="cal-panel-remove-lift"
              >
                {repeats ? 'Remove every run' : 'Remove from the calendar'}
              </button>
            )}
          </div>
        )
      )}
    </aside>
  );
}

function SitePanel(props: LanePanelProps & { site: SiteBar }) {
  const { site, timeZone } = props;
  // The window whose Remove was pressed: another bar selected, and its
  // own Remove starts unasked.
  const [confirming, setConfirming] = useState<string | null>(null);
  // Where Keep sends focus: the Remove button, drawn again (BB-B-2).
  const removeRef = useRef<HTMLButtonElement>(null);
  const when = `${formatZonedDateTime(site.startMs, timeZone)} → ${
    site.ongoing ? 'still open' : formatZonedDateTime(site.endMs, timeZone)
  } ${timeZoneLabel(timeZone, site.startMs)}`;
  return (
    <aside className="nx-lane-panel" aria-label="Selected site event" data-testid="cal-panel">
      <div className="nx-lane-panel__top">
        <span className="nx-lane-panel__eyebrow">Site</span>
        <CloseButton onClose={props.onClose} />
      </div>
      <h3 className="nx-lane-panel__title" data-testid="cal-panel-title">
        {site.kind === 'maintenance' ? 'Blocked time' : site.label}
      </h3>
      <div className="nx-lane-panel__sep">
        <Row k="When" v={when} />
        {site.maintenance && <Row k="Reason" v={site.maintenance.reason || '—'} />}
        {site.maintenance && (
          <Row
            k="Blocked by"
            v={
              <span data-testid="cal-panel-blocked-by">
                {standaloneLabel(creatorLabel(site.maintenance, props.mySub))}
              </span>
            }
          />
        )}
        {site.fire && (
          <>
            <Row
              k="Status"
              strong
              v={
                site.ongoing
                  ? 'Open now'
                  : site.fire.resolution === 'cancel_all'
                    ? 'Resolved — work cancelled'
                    : 'Resolved — work resumed'
              }
            />
            <Row k="Tasks stopped" v={site.fire.interrupted_task_ids.length} />
            {site.fire.notes && <Row k="Notes" v={site.fire.notes} />}
          </>
        )}
      </div>
      <p className="nx-lane-panel__note">
        {site.kind === 'maintenance'
          ? 'Nothing is dispatched during this period, and scheduled tasks that come due are skipped. Work already running finishes and the robots dock.'
          : site.fire?.drill
            ? 'A fire drill: robots pulled over and dispatch was frozen until it was resolved.'
            : 'A fire alarm: robots pulled over and dispatch was frozen until it was resolved.'}
      </p>
      {site.maintenance &&
        props.onDeleteMaintenance &&
        (confirming === site.maintenance.window_id ? (
          <RemoveConfirm
            back={removeRef}
            onKeep={() => setConfirming(null)}
            onRemove={() => {
              setConfirming(null);
              props.onDeleteMaintenance!(site.maintenance!);
            }}
          />
        ) : (
          <div className="nx-lane-panel__actions">
            {/* "Blocked time", as the rest of the calendar says (review
                BC-F16, 2026-10-31): this was the last "window". */}
            <button
              ref={removeRef}
              type="button"
              className="nx-lane-btn nx-lane-btn--danger"
              onClick={() => setConfirming(site.maintenance!.window_id)}
              data-testid="cal-panel-remove-maint"
            >
              Remove this blocked time
            </button>
          </div>
        ))}
    </aside>
  );
}

/** Keep / Remove it, in place of "Remove this blocked time": brought
 *  into the panel's view and focused on Keep, as the panel's other
 *  questions are (`useRevealOnMount`), so Enter held on Remove keeps it.
 *  Keep hands focus back to the Remove button (`back`). */
function RemoveConfirm({
  onKeep,
  onRemove,
  back,
  removeLabel = 'Remove it',
}: {
  onKeep: () => void;
  onRemove: () => void;
  back: RefObject<HTMLButtonElement | null>;
  removeLabel?: string;
}) {
  const ref = useRevealOnMount<HTMLDivElement>(undefined, back);
  return (
    <div
      ref={ref}
      className="nx-lane-panel__actions"
      role="group"
      aria-label="Confirm remove"
      data-testid="cal-panel-remove-maint-ask"
    >
      <button type="button" className="nx-lane-btn" onClick={onKeep}>
        Keep
      </button>
      <button
        type="button"
        className="nx-lane-btn nx-lane-btn--danger"
        onClick={onRemove}
        data-testid="cal-panel-remove-maint-confirm"
      >
        {removeLabel}
      </button>
    </div>
  );
}
